// app/javascript/controllers/wiki_sortable_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["list"]
    static values = {
        url: String,
        type: { type: String, default: "page" }, // "section" or "page"
        group: { type: String, default: "" },
        nested: { type: Boolean, default: false }
    }

    // sortablejs only serves the manage wiki tree; fetched from its own chunk on connect.
    async connect() {
        this.Sortable = (await import("sortablejs")).default
        if (this.element.isConnected) this.initSortable()
    }

    disconnect() {
        if (this.sortable) {
            this.sortable.destroy()
        }
    }

    initSortable() {
        const options = {
            animation: 150,
            handle: '.wiki-drag-handle',
            ghostClass: 'wiki-sortable-ghost',
            chosenClass: 'wiki-sortable-chosen',
            dragClass: 'wiki-sortable-drag',
            fallbackOnBody: true,
            swapThreshold: 0.65,
            onEnd: this.handleSort.bind(this),
            onMove: this.handleMove.bind(this)
        }

        // If group is specified, enable cross-list dragging
        if (this.groupValue) {
            options.group = {
                name: this.groupValue,
                pull: true,
                put: true
            }
        }

        if (this.hasListTarget) {
            this.sortable = new this.Sortable(this.listTarget, options)
        }
    }

    handleMove(evt) {
        // Prevent nesting beyond allowed depth if needed
        if (this.nestedValue) {
            const depth = this.getDepth(evt.to)
            if (depth > 3) {
                return false
            }
        }
        return true
    }

    getDepth(element) {
        let depth = 0
        let parent = element
        while (parent && parent !== this.element) {
            if (parent.dataset.wikiSortableTarget === 'list') {
                depth++
            }
            parent = parent.parentElement
        }
        return depth
    }

    async handleSort(event) {
        const { item, newIndex, from, to } = event

        const itemId = item.dataset.id
        const itemType = item.dataset.type || this.typeValue

        // Collect new order
        const ids = Array.from(event.to.children)
            .filter(el => el.dataset.id)
            .map(el => el.dataset.id)

        // Build request body
        const body = {
            ids: ids,
            position: newIndex + 1
        }

        // If moved to a different list (different parent section)
        if (from !== to && to.dataset.parentId) {
            body.parent_id = to.dataset.parentId
        }

        try {
            const response = await fetch(this.urlValue, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': this.csrfToken,
                    'Accept': 'application/json'
                },
                body: JSON.stringify(body)
            })

            if (!response.ok) {
                throw new Error('Sort failed')
            }
        } catch (error) {
            console.error('Sort error:', error)
            // Revert the sort visually
            this.sortable.sort(this.originalOrder)
        }
    }

    get csrfToken() {
        return document.querySelector("[name='csrf-token']")?.content || ''
    }

    // Store original order before drag starts
    onStart() {
        this.originalOrder = this.sortable.toArray()
    }
}
